import React, { PureComponent } from "react";

import Uri from "jsuri";

import {
  BarChart,
  Bar,
  Cell,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  ReferenceDot,
} from "recharts";

import missing_image from "i/missing_image.svg";

import {
  MetricTable,
  MAIN_COLORS,
  CustomizedYAxisTick,
  StandardTooltip,
  selectFormat,
  Error,
  formatDateFromData,
  isDateFromDataInISOFormat,
  customColorIndex,
  getAllKeys,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  RenderBackground,
  InsightsTickIndicator,
  closeAllBarchartTooltips,
} from "components/Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XrayUtils.js";

import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

const metricTableBarChart = (cell) => (
  <MetricTable {...cell} graph={cell.chart} graphType="bar" />
);

class VisitsBySource extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };
    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }
  colorsIndex = () => new Uri(this.props.url).getQueryParamValue("colors");

  clickBar(d) {
    if (this.state.tooltip.show) {
      this.setState({
        tooltip: { ...d, show: false },
      });
    } else {
      this.setState({
        tooltip: { ...d, show: true },
      });
    }
  }

  closeTooltip() {
    this.setState({
      tooltip: { show: false },
    });
  }

  handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  renderBars = (data) => {
    if (data === undefined) {
      return [];
    }
    let bars = [];
    let labels = [];
    const sortedKeys = this.getSortedKeys(data);
    const insights = this.state.widgetsInsights;
    const isFixedBarWidth = data.length <= 7;

    [...new Set(sortedKeys)].forEach((bar, i) => {
      bars.push(
        <Bar
          key={i}
          dataKey={bar}
          stackId="stack"
          barSize={isFixedBarWidth ? 35 : null}
          fill={MAIN_COLORS[customColorIndex(i, this.colorsIndex())]}
          isAnimationActive={false}
          cursor="pointer"
          // only first bar needs background
          background={
            i === 0 ? (
              <RenderBackground
                insights={insights}
                showIndicator={!this.state.tooltip.show}
                node={this.node}
                i={i}
              />
            ) : null
          }
          onClick={(d) => {
            const label = labels.filter((l) => l.date === d.date);
            const extra = sortedKeys
              .map((k, j) => ({
                key: k,
                value: d[k],
                color: MAIN_COLORS[customColorIndex(j, this.colorsIndex())],
              }))
              .filter((o) => o.value !== undefined);

            d.extra = extra;
            d.insight = label.length ? label[0] : null;

            this.clickBar(d, bar);
          }}
        >
          {data.map(
            (entry, index) =>
              entry && (
                <Cell
                  key={`cell-${bar}-${index}`}
                  fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                ></Cell>
              )
          )}
        </Bar>
      );
    });

    return bars;
  };

  legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="legend mt-2 mt-md-0">
        <span
          className="legend__icon"
          style={{ background: MAIN_COLORS[i] }}
        ></span>
        <span className="legend__text" style={{ color: MAIN_COLORS[i] }}>
          {entry.replace("_", " ")}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    if (data === undefined) {
      return [];
    }
    let legends = [];
    let all_legends = [];

    const sortedKeys = this.getSortedKeys(data);

    for (let i = 0, j = data.length; i <= j - 1; i++) {
      sortedKeys.forEach((source) => all_legends.push(source));
    }

    legends = [...new Set(all_legends)];
    return this.legendElement(legends);
  };

  getSortedKeys = (_data) => {
    if (!_data || !_data[0]) return [];

    const data = _data.filter((d) => d);

    const keys = getAllKeys(data, ["date", "domains", "videos", "articles"]);
    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  };

  getExportData = () => {
    return {
      data: this.props.data.data,
      opts: {
        type: "chart",
        date: {
          // overwrites the default settings for date only when the date is in ISO format
          defined: isDateFromDataInISOFormat(this.props.data.data),
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: "YYYY-MM-DD",
        },
      },
    };
  };

  render() {
    const { data, title } = this.props;
    const { widgetsInsights } = this.state;

    const widget_data = data.data;
    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = formatDateFromData(widget_data);

    return (
      <div ref={this.divPosition}>
        {this.renderLegends(preparedData)}
        <div className="d-flex mt-2">
          <ResponsiveContainer height={360} ref={this.props.childRef}>
            <BarChart
              data={preparedData}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              stackOffset="sign"
              margin={{ top: 20, right: 0, left: 0, bottom: 15 }}
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />
              {this.renderBars(preparedData)}
              <XAxis dataKey="date" tickLine={false} axisLine={false} />
              <YAxis
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTick />}
              />
              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          // Articles/Videos
                          const contentType =
                            tooltip_data.articles || tooltip_data.videos;

                          const contentTypeName = tooltip_data.articles
                            ? "articles"
                            : "videos";

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {Object.keys(payload)
                                  .sort(
                                    (a, b) =>
                                      payload[b].value - payload[a].value
                                  )
                                  .filter((key) => payload[key].value > 0)
                                  .map((key) => (
                                    <div
                                      className="standard-tooltip__item"
                                      key={key}
                                    >
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: payload[key].color,
                                        }}
                                      ></span>
                                      <span
                                        className="standard-tooltip__key"
                                        style={{
                                          color: payload[key].color,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {payload[key].dataKey.replace("_", " ")}{" "}
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "number",
                                          payload[key].value
                                        )}
                                      </span>
                                    </div>
                                  ))}

                                {contentType && (
                                  <>
                                    <div className="standard-tooltip__dilimiter2 mt-3"></div>
                                    <div className="standard-tooltip__section-header my-2">
                                      The most popular {contentTypeName} on this
                                      day.
                                    </div>
                                  </>
                                )}

                                {contentType.map((content, index) => {
                                  if (!content) return null;

                                  let IMAGE = {
                                    backgroundImage: `url(${missing_image})`,
                                  };
                                  if (content.metadata_thumb_url) {
                                    IMAGE = {
                                      backgroundImage: `url(${content.metadata_thumb_url})`,
                                    };
                                  } else {
                                    IMAGE = {
                                      backgroundImage: `url(${missing_image})`,
                                    };
                                  }

                                  return (
                                    <div
                                      className="standard-tooltip__details--posts"
                                      key={"content_" + index}
                                    >
                                      <article className="content-table">
                                        <figure className="content-table__figure">
                                          <div
                                            className="content-table__img"
                                            style={IMAGE}
                                          ></div>
                                        </figure>

                                        <section className="content-table__detail">
                                          <span className="standard-tooltip__value">
                                            {metricTableBarChart(content)}
                                          </span>
                                        </section>
                                      </article>

                                      <div className="standard-tooltip__text">
                                        {content.title}
                                      </div>
                                      {contentType.length - 1 > index ? (
                                        <div className="standard-tooltip__dilimiter2"></div>
                                      ) : null}
                                    </div>
                                  );
                                })}

                                {widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => (
                  <ReferenceDot
                    x={o.date}
                    y={0}
                    key={title + index}
                    ifOverflow="extendDomain"
                    shape={
                      <InsightsTickIndicator
                        insights={widgetsInsights}
                        date={o.date}
                        node={this.node}
                      />
                    }
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>

        <div
          className="xray-tooltip-container"
          style={{
            top: "5.4rem",
            left:
              this.state.tooltip.x + this.state.tooltip.width + 220 <
              this.state.width
                ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                : this.state.tooltip.x -
                  getXrayTooltipPosition(
                    this.divPosition.current,
                    this.state.tooltip
                  ) +
                  "px",
            opacity: this.state.tooltip.show ? 1.0 : 0.0,
            visibility: this.state.tooltip.show ? "visible" : "hidden",
          }}
        >
          <XRayTooltip
            data={this.state.tooltip}
            insights={widgetsInsights}
            close={this.closeTooltip.bind(this)}
          />
        </div>
      </div>
    );
  }
}

export default VisitsBySource;
